import React from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { fetchPublicRedirects } from "@/lib/api";

/**
 * Catch-all route element.  On an unknown path, first waits for the
 * public redirects list to load, then:
 *  - if a redirect matches the current pathname, does the redirect
 *  - otherwise falls back to '/'
 *
 * Fixes the race where the '*' Route synchronously navigated to '/'
 * before <RedirectHandler>'s async query could resolve.
 */
export default function CatchAllRedirect() {
    const location = useLocation();
    const { data, isFetched } = useQuery({
        queryKey: ["public-redirects"],
        queryFn: fetchPublicRedirects,
        staleTime: 60 * 1000,
    });

    if (!isFetched) {
        // Tiny placeholder while the redirect list is loading.  Prevents
        // the synchronous fallback to '/' from firing before we know
        // whether the visited URL is a configured redirect.
        return (
            <div className="min-h-screen bg-paper flex items-center justify-center">
                <div className="font-sub text-[11px] uppercase tracking-[0.2em] text-black/40 animate-pulse">
                    Loading&hellip;
                </div>
            </div>
        );
    }

    const items = data?.items || [];
    const hit = items.find((r) => r.from_path === location.pathname);
    if (hit && hit.to_url) {
        const to = hit.to_url;
        if (/^https?:\/\//i.test(to)) {
            // External URL — full replace outside the SPA.
            if (typeof window !== "undefined") window.location.replace(to);
            return null;
        }
        const target = to.startsWith("/") ? to : `/${to}`;
        return <Navigate to={target} replace />;
    }

    return <Navigate to="/" replace />;
}
